/**
 * ImageToolbar (editor-image-tools) — ngwEditor 본문에서 이미지를 클릭(NodeSelection)하면 이미지
 * 위/아래에 뜨는 Floating 툴바. Figma floating action bar 디자인(node 81205:452) 적용:
 *
 *   [↺ 왼쪽회전] [↻ 오른쪽회전] │ [⇋ 좌우반전] [⇵ 상하반전] │ [⤢ 크기 ▾]
 *                                                            └ 자동 ✓ / 작게(25%) / 중간(50%) / 크게(100%)
 *
 * 설계(옵션 B, design §4):
 *   - 어댑터(`editorImage`)의 `subscribeSelection` 으로 선택 상태를 구독하고, 선택된 이미지의
 *     `getBoundingClientRect()` 기준으로 portal(document.body, position: fixed) 위치를 계산.
 *   - 회전/반전은 내장 커맨드 wrapper(`rotate`/`flip`)를 호출 → 내장 CSS transform 즉시 반영.
 *     실제 픽셀 회전은 송신 시 `prepareForSend`(어댑터 canvas) 에서 수행.
 *   - 크기는 프리셋(자동/25/50/100) Dropdown → `setSize`. 현재 크기에 체크 표시.
 *   - 외부 http(s) 이미지처럼 canvas 픽셀화 불가한 경우(`rotatable === false`)는 회전/반전 버튼을
 *     비활성화하고 경고 툴팁을 표시(silent loss 방지). 네이티브 `disabled` 대신 `aria-disabled` 를
 *     사용해 비활성 상태에서도 툴팁이 뜨도록 한다.
 */

import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Dropdown, Tooltip } from 'antd'
import type { MenuProps } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import i18n from 'i18n'
import type { IEditorImage, SelectedImageState, ImageSizePreset } from 'libs/editor/IEditorImage'
import rotateLeftIcon from 'assets/images/editor/rotate-left.svg'
import rotateRightIcon from 'assets/images/editor/rotate-right.svg'
import flipVerticalIcon from 'assets/images/editor/flip-vertical.svg'
import flipHorizontalIcon from 'assets/images/editor/flip-horizontal.svg'
import imageScaleIcon from 'assets/images/editor/image-scale.svg'
import chevronDownIcon from 'assets/images/editor/chevron-down.svg'
import styles from './ImageToolbar.module.scss'

interface ImageToolbarProps {
  /** 어댑터 image 표면 (NgwEditor: 실제 동작 / TinyMCE: null 구독) */
  editorImage: IEditorImage
  /** ngwEditor 모드에서만 활성 */
  enabled: boolean
}

/** 툴바 전체 높이(px) — 버튼 32 + 상하 padding 4×2 + border 1×2 (Figma 81205:452). */
const BAR_HEIGHT = 42
/** 툴바 전체 폭(px) — 좌우 clamp 용. padding 16 + 버튼 32×4 + 구분선 16×2 + 크기버튼 48 + border 2. */
const BAR_WIDTH = 226
const GAP = 8
const Z_INDEX = 900 // antd Modal(1000) 보다 낮게 — popup 위 에디터에서도 모달에 가리도록

interface BarPosition {
  top: number
  left: number
}

/**
 * 선택 이미지가 속한 스크롤 컨테이너(에디터 스크롤 영역)의 가시 영역 상/하단(viewport 좌표)을 구한다.
 * 스크롤 가능한 조상이 없으면 뷰포트(0 ~ innerHeight)를 반환. 이미지의 상/하단이 이 영역을 벗어나면
 * "잘림"으로 판정해 툴바 위치를 결정한다.
 */
function getVisibleBounds(el: HTMLElement): { top: number; bottom: number } {
  let node = el.parentElement
  while (node != null && node !== document.body && node !== document.documentElement) {
    const style = getComputedStyle(node)
    if (/(auto|scroll|overlay)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) {
      const rect = node.getBoundingClientRect()
      return { top: rect.top, bottom: rect.bottom }
    }
    node = node.parentElement
  }
  return { top: 0, bottom: window.innerHeight }
}

interface ToolButtonProps {
  title: string
  iconSrc: string
  disabled: boolean
  onClick: () => void
}

/** 32×32 원형 아이콘 버튼(아이콘 16×16). aria-disabled 로 비활성 시에도 툴팁 표시. */
function ToolButton({ title, iconSrc, disabled, onClick }: ToolButtonProps): JSX.Element {
  return (
    <Tooltip title={title}>
      <button
        type="button"
        className={styles.iconButton}
        aria-disabled={disabled}
        onClick={disabled ? undefined : onClick}
      >
        <img src={iconSrc} alt="" width={16} height={16} />
      </button>
    </Tooltip>
  )
}

export default function ImageToolbar({
  editorImage,
  enabled,
}: ImageToolbarProps): JSX.Element | null {
  const [selected, setSelected] = useState<SelectedImageState | null>(null)
  const [position, setPosition] = useState<BarPosition | null>(null)

  // 선택 상태 구독.
  useEffect(() => {
    if (!enabled) {
      setSelected(null)
      return
    }
    setSelected(editorImage.getSelectedImage())
    return editorImage.subscribeSelection(setSelected)
  }, [editorImage, enabled])

  // 위치 계산 + 스크롤/리사이즈 재계산.
  useLayoutEffect(() => {
    if (selected == null) {
      setPosition(null)
      return
    }
    const compute = (): void => {
      const rect = selected.dom.getBoundingClientRect()
      const vis = getVisibleBounds(selected.dom)
      const topClipped = rect.top < vis.top // 이미지 윗쪽이 가시 영역 위로 잘림
      const bottomClipped = rect.bottom > vis.bottom // 이미지 아랫쪽이 가시 영역 아래로 잘림
      const above = rect.top - BAR_HEIGHT - GAP
      const below = rect.bottom + GAP

      let top: number
      if (topClipped && bottomClipped) {
        // 위·아래 모두 잘림 → 보이는 부분 상단 위(가시 영역 안쪽)에 표시.
        top = vis.top + GAP
      } else if (topClipped) {
        // 윗쪽이 안 보임 → 이미지 하단에. 단 하단도 가시 영역을 벗어나면 보이는 부분 위로.
        top = below + BAR_HEIGHT <= vis.bottom ? below : vis.top + GAP
      } else if (bottomClipped) {
        // 아랫쪽이 안 보임 → 이미지 상단에. 단 위 공간이 부족하면 보이는 부분 위로.
        top = above >= vis.top ? above : vis.top + GAP
      } else {
        // 전체가 보임 → 상단 우선, 위 공간 없으면 하단.
        top = above >= vis.top ? above : below
      }

      const maxLeft = Math.max(0, window.innerWidth - BAR_WIDTH)
      const left = Math.min(Math.max(0, rect.left), maxLeft)
      setPosition({ top, left })
    }
    compute()
    window.addEventListener('scroll', compute, true)
    window.addEventListener('resize', compute)
    return () => {
      window.removeEventListener('scroll', compute, true)
      window.removeEventListener('resize', compute)
    }
  }, [selected])

  const onRotateLeft = useCallback(() => editorImage.rotate('left'), [editorImage])
  const onRotateRight = useCallback(() => editorImage.rotate('right'), [editorImage])
  const onFlipV = useCallback(() => editorImage.flip('vertical'), [editorImage])
  const onFlipH = useCallback(() => editorImage.flip('horizontal'), [editorImage])
  const onSelectSize: MenuProps['onClick'] = useCallback(
    (info: { key: string }) => {
      const size = Number.parseInt(info.key, 10) as ImageSizePreset
      editorImage.setSize(size)
    },
    [editorImage],
  )

  if (selected == null || position == null) {
    return null
  }

  const rotatable = selected.rotatable
  const disabledTip = rotatable ? '' : i18n.t('editor.image.rotateUnavailable')
  const currentSize = selected.size

  // 현재 크기에 체크 표시(캡처의 자동 ✓ 대응).
  const sizeLabel = (text: string, key: ImageSizePreset): JSX.Element => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 24,
        minWidth: 96,
      }}
    >
      <span>{text}</span>
      {currentSize === key ? <CheckOutlined /> : null}
    </div>
  )
  const sizeItems: MenuProps['items'] = [
    { key: '0', label: sizeLabel(i18n.t('editor.image.size.auto'), 0) },
    { key: '1', label: sizeLabel(i18n.t('editor.image.size.small'), 1) },
    { key: '2', label: sizeLabel(i18n.t('editor.image.size.medium'), 2) },
    { key: '3', label: sizeLabel(i18n.t('editor.image.size.large'), 3) },
  ]

  // mousedown 단계에서 selection 해제 방지(에디터 NodeSelection 유지).
  const preventBlur = (e: React.MouseEvent): void => {
    e.preventDefault()
  }

  const toolbar = (
    <div
      role="toolbar"
      onMouseDown={preventBlur}
      className={styles.toolbar}
      style={{ top: position.top, left: position.left, zIndex: Z_INDEX }}
    >
      <ToolButton
        title={rotatable ? i18n.t('editor.image.rotateLeft') : disabledTip}
        iconSrc={rotateLeftIcon}
        disabled={!rotatable}
        onClick={onRotateLeft}
      />
      <ToolButton
        title={rotatable ? i18n.t('editor.image.rotateRight') : disabledTip}
        iconSrc={rotateRightIcon}
        disabled={!rotatable}
        onClick={onRotateRight}
      />
      <div className={styles.separator} />
      <ToolButton
        title={rotatable ? i18n.t('editor.image.flipH') : disabledTip}
        iconSrc={flipHorizontalIcon}
        disabled={!rotatable}
        onClick={onFlipH}
      />
      <ToolButton
        title={rotatable ? i18n.t('editor.image.flipV') : disabledTip}
        iconSrc={flipVerticalIcon}
        disabled={!rotatable}
        onClick={onFlipV}
      />
      <div className={styles.separator} />
      <Dropdown menu={{ items: sizeItems, onClick: onSelectSize }} trigger={['click']}>
        <Tooltip title={i18n.t('editor.image.size.label')}>
          <button type="button" className={styles.scaleButton}>
            <img src={imageScaleIcon} alt="" width={16} height={16} />
            <img src={chevronDownIcon} alt="" width={16} height={16} />
          </button>
        </Tooltip>
      </Dropdown>
    </div>
  )

  return createPortal(toolbar, document.body)
}
